import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Queries } from '..';
import { DEFAULT_TIME_FIELD_SCROLL_TO_TIME } from '../constants';
import { getScheduleInfoFromRRule } from '../helpers';

// Rounds time to next 15 minute interval, 2:18 PM -> 2:30 PM, 2:00 PM -> 2:00 PM
const roundToNext15Minutes = (time: dayjs.Dayjs) => {
  const minutes = time.minute();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  return time.minute(roundedMinutes).second(0).format('hh:mm A');
};

export const useGetTimeFieldStartTime = ({ date, locationId }: { date?: string; locationId: string }) => {
  const { data: scheduleData } = Queries.useListSchedulesQuery({
    request: { ids: [locationId] },
    options: { enabled: !!locationId && dayjs(date).isValid() },
  });

  const startTime = useMemo(() => {
    if (!date || !locationId || !scheduleData) return DEFAULT_TIME_FIELD_SCROLL_TO_TIME;

    const selectedDate = dayjs(date);
    const isToday = selectedDate.isSame(dayjs(), 'day');
    const now = dayjs();

    // Get office start time for the selected date
    const officeSchedule = scheduleData.schedules?.find((schedule) => schedule.locationId === locationId);
    const dateDay = selectedDate.day();

    const workingHours =
      officeSchedule?.recurrenceRules?.workingHours?.flatMap(({ rrule, duration }) => {
        if (!rrule) return [];
        const { startTime: officeTime, daysOfWeek } = getScheduleInfoFromRRule(rrule, duration, 'hh:mm A');
        return daysOfWeek.includes(dateDay) ? [officeTime] : [];
      }) ?? [];

    const officeStartTime = workingHours[0];

    if (isToday) {
      const nextRounded = roundToNext15Minutes(now);

      if (officeStartTime) {
        const officeStart = dayjs(officeStartTime, 'hh:mm A');
        const currentTime = dayjs(nextRounded, 'hh:mm A');

        return currentTime.isBefore(officeStart) ? officeStartTime : nextRounded;
      }

      return nextRounded;
    } else {
      return officeStartTime || DEFAULT_TIME_FIELD_SCROLL_TO_TIME;
    }
  }, [date, locationId, scheduleData]);

  return dayjs(startTime, 'hh:mm A').format('HH:mm:ss');
};
